type ReplaceState = History['replaceState'];
type PushState = History['pushState'];
type Guarded<T> = T & {
  __preserveHash?: true;
  __preserveHashPage?: string;
  __runPreservingHash?: (refresh: () => void) => () => void;
  __restorePreservingHash?: () => void;
};

type NextHistoryState = {
  __NA?: boolean;
};

const isNextHistoryState = (data: unknown): data is NextHistoryState =>
  typeof data === 'object' &&
  data !== null &&
  (data as NextHistoryState).__NA === true;

const noop = () => {};

/**
 * Runs a Next.js App Router `refresh()` while preserving the current URL hash.
 *
 * `router.refresh()` makes the App Router re-sync the browser URL to its
 * internal `canonicalUrl` (pathname + search only) via `history.replaceState`.
 * The App Router state does not track the client-side `#…` fragment, so the
 * refresh drops it. On hash-driven search / listing pages the fragment *is*
 * the search state (query + facets), so losing it collapses the page to a bare
 * `/search`. This intermittently broke the "reflect currency change in search
 * results" e2e (a race between the refresh's async
 * `replaceState` and the follow-up navigation) and can lose a real user's
 * query/filters on a currency switch. See CPS-7492.
 *
 * The refresh's URL-sync is asynchronous, so we wrap the history write methods
 * until Next completes the refresh. The returned cleanup lets the caller tie
 * the guard to React's transition lifecycle even when Next coalesces refreshes
 * and emits fewer history writes than refresh calls.
 *
 * The wrapper is deliberately narrow so it can't hijack unrelated writes while
 * active:
 *  - only when the current hash carries serialized state (contains `=`, e.g.
 *    `#q=dogs&nf-ec_price_filter=…`) — bare anchors like `#reviews` are ignored;
 *  - only App Router writes (`history.state.__NA`) whose target equals the
 *    current `pathname + search`; Coveo's own history writes remain untouched;
 *  - it re-appends the *live* fragment (not one captured at call time), so if
 *    the user changed the search state during the window the current state is
 *    preserved, not a stale snapshot;
 *  - rapid currency changes share one guard that tracks their outstanding
 *    refresh writes instead of stacking history wrappers.
 *
 * App Router 14.2.26 re-syncs via `replaceState`; `pushState` is guarded too so
 * a future switch to `pushState` doesn't silently reintroduce the drop. Next's
 * `__NA` marker also distinguishes this write from an intentional hash clear,
 * so the guard can remain active for the full refresh without a timeout race.
 * `__NA` is a private Next.js detail; the searchV2 currency-switch e2e is the
 * upgrade canary for this coupling.
 *
 * @returns An idempotent cleanup callback. Call it when the refresh transition
 * settles so a coalesced refresh that emits no history write cannot leave the
 * guard installed.
 */
export function refreshPreservingHash(refresh: () => void): () => void {
  if (typeof window === 'undefined') {
    refresh();
    return noop;
  }

  const activeGuard = window.history.replaceState as Guarded<ReplaceState>;
  if (activeGuard.__preserveHash && activeGuard.__runPreservingHash) {
    const currentPage = `${window.location.pathname}${window.location.search}`;
    if (activeGuard.__preserveHashPage === currentPage) {
      // A guard from a previous (still-pending) refresh is already installed on
      // this page. Reuse it even if the live hash was cleared in the meantime:
      // returning its cleanup prevents a newer hash-less refresh from
      // orphaning the old guard.
      return activeGuard.__runPreservingHash(refresh);
    }

    // A client navigation happened while the previous refresh was pending.
    // Its guard captured the old pathname, so replace it before refreshing the
    // new hash-driven page. The old cleanup is idempotent and cannot tear down
    // the fresh guard when its transition eventually settles.
    activeGuard.__restorePreservingHash?.();
    return refreshPreservingHash(refresh);
  }

  const { hash } = window.location;
  if (!hash || !hash.includes('=')) {
    refresh();
    return noop;
  }

  const originalReplace = window.history.replaceState.bind(window.history);
  const originalPush = window.history.pushState.bind(window.history);
  const samePage = `${window.location.pathname}${window.location.search}`;

  let restored = false;
  let pendingRefreshes = 0;
  const restore = () => {
    if (restored) return;
    restored = true;
    window.history.replaceState = originalReplace;
    window.history.pushState = originalPush;
  };
  const isSamePageWithoutHash = (url: string | URL | null | undefined) => {
    if (url == null) return false;
    const target = new URL(String(url), window.location.href);
    return (
      target.origin === window.location.origin &&
      `${target.pathname}${target.search}` === samePage &&
      target.hash === ''
    );
  };

  const guard = <T extends ReplaceState | PushState>(
    original: T
  ): Guarded<T> => {
    const wrapped = ((
      data: unknown,
      unused: string,
      url?: string | URL | null
    ) => {
      if (isNextHistoryState(data) && isSamePageWithoutHash(url)) {
        // The App Router re-synced this page's URL without the fragment. Put
        // the *live* fragment back (it may have changed since install), then
        // consume one of the outstanding refresh writes.
        const liveHash = window.location.hash;
        if (liveHash && liveHash.includes('=')) {
          original(data, unused, `${samePage}${liveHash}`);
          pendingRefreshes -= 1;
          if (pendingRefreshes === 0) {
            restore();
          }
          return;
        }
      }
      original(data, unused, url);
    }) as Guarded<T>;
    wrapped.__preserveHash = true;
    wrapped.__preserveHashPage = samePage;
    return wrapped;
  };

  const guardedReplace = guard(originalReplace);
  const runPreservingHash = (guardedRefresh: () => void) => {
    const pendingBeforeRefresh = pendingRefreshes;
    pendingRefreshes += 1;
    try {
      guardedRefresh();
    } catch (err) {
      // A synchronous history write may already have consumed this refresh.
      // Only roll it back when its pending slot is still present.
      if (pendingRefreshes > pendingBeforeRefresh) {
        pendingRefreshes -= 1;
      }
      if (pendingRefreshes === 0) {
        restore();
      }
      throw err;
    }
    return restore;
  };
  guardedReplace.__runPreservingHash = runPreservingHash;
  guardedReplace.__restorePreservingHash = restore;

  window.history.replaceState = guardedReplace;
  window.history.pushState = guard(originalPush);

  return runPreservingHash(refresh);
}
